import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useState } from 'react'
import { Link, useLocation, useNavigate } from 'react-router'
import { api, ApiError, type Item, type LockGrant, type SpaceMemberSummary } from '../../lib/api'
import { layout } from '../../design/tokens'
import { ChampCode } from './ChampCode'
import { lienDuCode } from './lienDuCode'

/**
 * Cadenas au trait fin, dans le style des icônes d'outil : la fiche protégée doit se reconnaître
 * d'un coup d'œil sans qu'un pictogramme en couleur vienne trancher sur la carte.
 */
export function Cadenas({ taille = 16, couleur }: { taille?: number; couleur?: string }) {
  return (
    <svg
      width={taille}
      height={taille}
      viewBox="0 0 24 24"
      fill="none"
      stroke={couleur ?? 'currentColor'}
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className="shrink-0"
    >
      <rect x="5" y="10.5" width="14" height="10.5" rx="2" />
      <path d="M8.5 10.5V7.5a3.5 3.5 0 0 1 7 0v3" />
    </svg>
  )
}

/**
 * L'état d'une fiche sous verrou, du côté de l'écran.
 *
 * Le contenu ouvert vit <b>ici</b>, dans l'état du composant, et non dans le cache des requêtes.
 * C'est ce qui tient la règle demandée : sortir de la fiche efface tout, et y revenir redemande
 * le code. Un cache, même court, l'aurait rouverte d'elle-même.
 *
 * Le code saisi est retenu le temps de la visite, pour une seule raison : après chaque
 * modification il faut relire la fiche, et le serveur ne la rend qu'avec le code. Le redemander
 * à chaque bloc enregistré aurait rendu l'édition impraticable.
 */
export function useVerrou(spaceId: string, itemId: string) {
  const [code, setCode] = useState<string | null>(null)
  const [ouverte, setOuverte] = useState<Item | null>(null)
  const [erreur, setErreur] = useState<string | null>(null)

  const ouvrir = useMutation({
    mutationFn: (saisi: string) => api.openItem(spaceId, itemId, saisi),
    onSuccess: (fiche, saisi) => {
      setCode(saisi)
      setOuverte(fiche)
      setErreur(null)
    },
    onError: (cause: unknown) =>
      setErreur(cause instanceof ApiError ? cause.message : "L'ouverture a échoué."),
  })

  const relire = useCallback(async () => {
    if (code === null) {
      return
    }

    try {
      setOuverte(await api.openItem(spaceId, itemId, code))
    } catch {
      // Le code ne passe plus : il a changé, ou le verrou a été retiré d'ailleurs. On referme
      // plutôt que de garder à l'écran un contenu dont on ne répond plus.
      setCode(null)
      setOuverte(null)
    }
  }, [code, itemId, spaceId])

  const refermer = useCallback(() => {
    setCode(null)
    setOuverte(null)
    setErreur(null)
  }, [])

  return { code, ouverte, erreur, ouvrir, relire, refermer }
}

export type Verrou = ReturnType<typeof useVerrou>

/**
 * Ce qu'on voit à la place d'une fiche verrouillée.
 *
 * L'écran entier, et non un bandeau au-dessus d'une fiche vidée : la fiche sans son contenu
 * donnerait à chercher ce qui manque, alors qu'il n'y a qu'une chose à faire.
 */
export function FicheProtegee({ item, verrou }: { item: Item; verrou: Verrou }) {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [saisie, setSaisie] = useState('')

  const ouvrir = () => {
    if (saisie.length === 4) {
      verrou.ouvrir.mutate(saisie)
      setSaisie('')
    }
  }

  return (
    <main
      className="mx-auto min-h-dvh bg-surface"
      style={{ maxWidth: layout.column, paddingInline: layout.gutter }}
    >
      <header className="flex items-center" style={{ height: layout.header }}>
        <button
          type="button"
          onClick={() => void navigate(-1)}
          aria-label="Retour"
          className="rounded-button border border-border bg-card"
          style={{ width: 44, height: 44 }}
        >
          ←
        </button>
      </header>

      <div className="mt-8 flex flex-col items-center text-center">
        <div
          className="flex items-center justify-center rounded-card bg-sunken"
          style={{ width: 72, height: 72 }}
        >
          <Cadenas taille={34} couleur="var(--color-ink-muted)" />
        </div>

        <h1 className="mt-5 font-serif text-record font-medium">{item.title}</h1>

        {item.canOpen ? (
          <p className="mt-2 text-meta text-ink-muted">Entrez votre code pour l’ouvrir.</p>
        ) : (
          <p className="mt-2 text-body text-ink-muted" style={{ maxWidth: '40ch' }}>
            {item.lockedByName
              ? `${item.lockedByName} a protégé cette fiche. Elle ou il peut vous en ouvrir l’accès.`
              : 'Cette fiche est protégée.'}
          </p>
        )}
      </div>

      {item.canOpen ? (
        <section className="mt-8">
          <ChampCode
            libelle="Votre code"
            valeur={saisie}
            onChange={setSaisie}
            onEntree={ouvrir}
            autoFocus
            enErreur={verrou.erreur !== null}
          />

          {verrou.erreur ? (
            <p className="mt-3 text-meta" style={{ color: 'var(--color-danger)' }}>
              {verrou.erreur}
            </p>
          ) : null}

          <button
            type="button"
            disabled={saisie.length !== 4 || verrou.ouvrir.isPending}
            onClick={ouvrir}
            className="mt-4 w-full rounded-button bg-accent text-card-title font-semibold text-card disabled:opacity-60"
            style={{ height: layout.primaryButtonHeight }}
          >
            {verrou.ouvrir.isPending ? 'Ouverture…' : 'Ouvrir'}
          </button>

          {/* Un code oublié ne se récupère pas, mais il se remplace : les fiches déjà protégées
              s'ouvriront avec le nouveau. Le dire ici évite de chercher ailleurs. */}
          <Link
            to={lienDuCode(pathname)}
            className="mt-4 block text-center text-meta font-semibold text-accent"
          >
            J’ai oublié mon code
          </Link>
        </section>
      ) : null}
    </main>
  )
}

/**
 * Poser le verrou, le retirer, et décider qui d'autre peut ouvrir.
 *
 * Vit dans le menu du crayon, avec les autres réglages de la fiche : protéger n'est pas une
 * action qu'on fait en passant, et la lui donner un bouton à l'en-tête l'aurait mise au même
 * rang que le favori.
 */
export function ReglagesDuVerrou({
  spaceId,
  item,
  membres,
  moi,
  aUnCode,
  estAdministration,
  verrou,
  onChange,
}: {
  spaceId: string
  item: Item
  membres: readonly SpaceMemberSummary[]
  /** Mon identifiant : on ne se confie pas une fiche à soi-même. */
  moi: string
  /** Faux : poser un verrou passe d'abord par l'écran qui crée le code. */
  aUnCode: boolean
  /** L'administration voit la fiche ouverte sans code : elle ne gère pas le verrou d'autrui. */
  estAdministration: boolean
  verrou: Verrou
  onChange: () => Promise<void> | void
}) {
  const { pathname } = useLocation()
  const queryClient = useQueryClient()
  const [codeRetrait, setCodeRetrait] = useState('')
  const [retraitOuvert, setRetraitOuvert] = useState(false)
  const [erreur, setErreur] = useState<string | null>(null)

  // Celui à qui l'accès a été confié ouvre la fiche sans gouverner son verrou : le lui laisser
  // reviendrait à permettre de reprendre une fiche qu'on lui a seulement prêtée.
  const estLeMien = item.isLocked && item.lockedByMe && !estAdministration

  const poser = useMutation({
    mutationFn: () => api.lockItem(spaceId, item.id),
    onSuccess: async () => {
      setErreur(null)
      // Même raison qu'à la création du code : la fiche en mémoire est encore ouverte, et la
      // marquer à relire la laisserait s'afficher le temps d'un aller-retour.
      queryClient.removeQueries({ queryKey: ['fiche', spaceId, item.id] })
      await onChange()
    },
    onError: (cause: unknown) =>
      setErreur(cause instanceof ApiError ? cause.message : 'La protection a échoué.'),
  })

  const retirer = useMutation({
    mutationFn: () => api.unlockItem(spaceId, item.id, codeRetrait),
    onSuccess: async () => {
      setErreur(null)
      setCodeRetrait('')
      setRetraitOuvert(false)
      verrou.refermer()
      await onChange()
    },
    onError: (cause: unknown) =>
      setErreur(cause instanceof ApiError ? cause.message : 'Le retrait a échoué.'),
  })

  // L'administration ne pose pas de verrou chez les autres : il tiendrait à **son** code, et le
  // propriétaire de l'espace se retrouverait enfermé dehors de sa propre fiche. Elle lit, elle
  // répare, elle ne s'approprie pas.
  if (estAdministration) {
    return item.isLocked ? (
      <p className="flex items-center gap-2 text-meta text-ink-muted">
        <Cadenas taille={15} />
        Fiche protégée par {item.lockedByName ?? 'un membre'} — ouverte ici par l’administration.
      </p>
    ) : null
  }

  if (!item.isLocked) {
    return (
      <>
        {aUnCode ? (
          <button
            type="button"
            onClick={() => poser.mutate()}
            disabled={poser.isPending}
            className="flex w-full items-center gap-2 rounded-field border border-border bg-card p-3 text-left text-body disabled:opacity-60"
          >
            <Cadenas />
            Protéger par un code
          </button>
        ) : (
          // Pas de code encore : on n'affiche pas un bouton qui refuserait. L'écran de création
          // ramène ici, où il ne restera qu'à cliquer.
          <Link
            to={lienDuCode(pathname, { espace: spaceId, fiche: item.id })}
            className="flex w-full items-center gap-2 rounded-field border border-border bg-card p-3 text-left text-body"
            style={{ textDecoration: 'none' }}
          >
            <Cadenas />
            Protéger par un code
            <span className="ml-auto text-meta text-ink-muted">créer mon code</span>
          </Link>
        )}
        {erreur ? (
          <p className="mt-2 text-meta" style={{ color: 'var(--color-danger)' }}>
            {erreur}
          </p>
        ) : null}
      </>
    )
  }

  if (!estLeMien) {
    return (
      <p className="flex items-center gap-2 text-meta text-ink-muted">
        <Cadenas taille={15} />
        Protégée par {item.lockedByName ?? 'un membre'}.
      </p>
    )
  }

  return (
    <div className="flex flex-col gap-3">
      <p className="flex items-center gap-2 text-meta font-semibold">
        <Cadenas taille={15} />
        Fiche protégée par votre code.
      </p>

      <Acces spaceId={spaceId} item={item} membres={membres} moi={moi} />

      {retraitOuvert ? (
        <div className="rounded-field border border-border bg-card p-3">
          <ChampCode
            libelle="Votre code, pour retirer la protection"
            valeur={codeRetrait}
            onChange={(valeur) => {
              setCodeRetrait(valeur)
              setErreur(null)
            }}
            onEntree={() => codeRetrait.length === 4 && retirer.mutate()}
            autoFocus
            enErreur={erreur !== null}
          />

          {erreur ? (
            <p className="mt-2 text-meta" style={{ color: 'var(--color-danger)' }}>
              {erreur}
            </p>
          ) : null}

          <button
            type="button"
            disabled={codeRetrait.length !== 4 || retirer.isPending}
            onClick={() => retirer.mutate()}
            className="mt-3 w-full rounded-button bg-accent text-meta font-semibold text-card disabled:opacity-60"
            style={{ height: 48 }}
          >
            {retirer.isPending ? 'Retrait…' : 'Retirer la protection'}
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setRetraitOuvert(true)}
          className="rounded-field border border-border bg-card p-3 text-left text-body"
        >
          Retirer la protection
        </button>
      )}
    </div>
  )
}

/**
 * À qui l'on confie l'accès.
 *
 * Confier n'est pas donner son code : l'autre ouvrira avec le sien. C'est ce qui permet de
 * partager une fiche sans partager toutes les autres.
 */
function Acces({
  spaceId,
  item,
  membres,
  moi,
}: {
  spaceId: string
  item: Item
  membres: readonly SpaceMemberSummary[]
  moi: string
}) {
  const acces = useQuery<readonly LockGrant[]>({
    queryKey: ['verrou-acces', spaceId, item.id],
    queryFn: () => api.lockGrants(spaceId, item.id),
  })

  const confier = useMutation({
    mutationFn: (userId: string) => api.grantLock(spaceId, item.id, userId),
    onSuccess: () => acces.refetch(),
  })

  const reprendre = useMutation({
    mutationFn: (userId: string) => api.revokeLock(spaceId, item.id, userId),
    onSuccess: () => acces.refetch(),
  })

  const donnes = acces.data ?? []

  // Les membres à qui l'on peut encore confier la fiche. Soi-même en est exclu : on l'ouvre déjà,
  // et le serveur ignorerait la demande sans rien dire — un bouton sans effet.
  const candidats = membres.filter(
    (membre) =>
      membre.userId !== moi && !donnes.some((donne) => donne.userId === membre.userId),
  )

  return (
    <section>
      <h3 className="text-label font-semibold uppercase text-ink-muted">Accès confié</h3>

      {donnes.length > 0 ? (
        <ul className="mt-2 flex flex-col gap-2">
          {donnes.map((donne) => (
            <li
              key={donne.userId}
              className="flex items-center gap-2 rounded-field border border-border bg-card px-3"
              style={{ height: 48 }}
            >
              <span className="grow truncate text-body">{donne.name}</span>
              <button
                type="button"
                onClick={() => reprendre.mutate(donne.userId)}
                className="text-meta font-semibold text-accent"
              >
                Reprendre
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="mt-2 text-meta text-ink-muted">Personne d’autre ne peut l’ouvrir.</p>
      )}

      {candidats.length > 0 ? (
        <ul className="mt-2 flex flex-wrap gap-2">
          {candidats.map((membre) => (
            <li key={membre.userId}>
              <button
                type="button"
                onClick={() => confier.mutate(membre.userId)}
                disabled={confier.isPending}
                className="rounded-button border border-border bg-card px-3 text-meta font-semibold disabled:opacity-60"
                style={{ height: 40 }}
              >
                + {membre.displayName}
              </button>
            </li>
          ))}
        </ul>
      ) : null}

      {/* Dit une fois, à l'endroit où l'on confie : sans cela, on croit donner son code. */}
      {donnes.length > 0 || candidats.length > 0 ? (
        <p className="mt-2 text-meta text-ink-muted">
          Chacun ouvre avec son propre code, jamais avec le vôtre.
        </p>
      ) : null}
    </section>
  )
}
